import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import _ from "lodash";

import { URLS } from "config/urls";
import {
  ArrowDropDownIcon,
  ArrowRightIcon,
  AutoAwesomeOutlinedIcon,
} from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import {
  scrollToSelectedAnchor,
  useRedirectUponFetchFailure,
} from "utils/common";
import { BOX_MAX_WIDTH } from "utils/constants";
import { reverse } from "utils/urls";

import Box from "components/Box";
import { ID as SUB_NAVIGATION_ID } from "components/DealPageLayout/SubNavigation";
import Divider from "components/Divider";
import Image from "components/Image";
import Loading from "components/Loading";
import { ID as MAIN_NAVIGATION_ID } from "components/MainNavigation";
import Paper from "components/Paper";
import Stack from "components/Stack";
import Text from "components/Text";
import { useAIMemoSection } from "components/useAIMemoSection";
import { usePageTitle } from "components/usePageTitle";

import { useSignedUser } from "entities/Auth/sdk";
import { useCompanySettings } from "entities/Company/sdk";
import { useProformaPDFGenerationReadyEvent } from "entities/Deal/hooks";
import { useDealDetails } from "entities/Deal/sdk";
import AppendixAccordion from "entities/Memo/components/AppendixAccordion";
import EditDocumentAdditionalHeaderInfoButton from "entities/Memo/components/EditDocumentAdditionalHeaderInfoButton";
import PageHeader from "entities/Memo/components/PageHeader";
import MemoSectionWrapper from "entities/Memo/components/SectionWrapper";
import SourceDocumentCollapse from "entities/Memo/components/SourceDocumentCollapse";
import SourceDocumentDrawer from "entities/Memo/components/SourceDocumentDrawer";
import MemoTabs from "entities/Memo/components/Tabs";
import { MemoTabChoices } from "entities/Memo/components/Tabs/constants";
import SavedPDFsTab from "entities/Memo/components/Tabs/SavedPDFsTab";
import {
  IMemoSection,
  useMemoDealDetails,
  useProformaPDFRetrieve,
  useSourceDocumentRetrieve,
  useSourceDocumentsForMemo,
} from "entities/Memo/sdk";
import { EMemoSectionType } from "entities/MemoTemplate/constants";
import { useMemoSectionAIExtractionProcessing } from "entities/TableStorage/hooks";

import "./memo.css";

interface IMemo {}

const ANCHORS = [MAIN_NAVIGATION_ID, SUB_NAVIGATION_ID];

const Memo: React.FC<IMemo> = () => {
  const [isAIMemoSectionDropdownOpen, setIsAIMemoSectionDropdownOpen] =
    useState<boolean>(false);
  const [disableGenerateButtonOnClick, setDisableGenerateButtonOnClick] =
    useState<boolean>(false);
  const [navigationTab, setNavigationTab] = useState<MemoTabChoices>(
    MemoTabChoices.CONTENT_EDITOR
  );

  const { data: companySettings } = useCompanySettings();

  const navigate = useNavigate();
  const { isSourceDocumentDrawerOpen, selectedSourceDocumentId } =
    useAIMemoSection();

  const params = useParams<{
    dealId: string;
    memoId: string;
  }>();
  const dealId = Number(params.dealId);
  const memoId = Number(params.memoId);

  const [searchParams, setSearchParams] = useSearchParams();

  const { data: proformaPDF, mutate: proformaPDFMutate } =
    useProformaPDFRetrieve({ dealId });

  const onProformaUpdate = useCallback(
    () => proformaPDFMutate(),
    [proformaPDFMutate]
  );

  useProformaPDFGenerationReadyEvent({
    successEventHandler: onProformaUpdate,
    failureEventHandler: onProformaUpdate,
  });

  const memoSectionIdParam = useMemo(
    () =>
      searchParams.get("memoSectionId")
        ? Number(searchParams.get("memoSectionId"))
        : null,
    [searchParams]
  );

  useEffect(() => {
    if (!_.isNil(memoId)) {
      setNavigationTab(MemoTabChoices.CONTENT_EDITOR);
    }
  }, [memoId]);

  const handleTabChange = useCallback(
    (_: any, tabIndex: MemoTabChoices) => {
      setNavigationTab(tabIndex);
      const urlParameters: URLSearchParams = new URLSearchParams();
      urlParameters.set("tab", tabIndex);
      setSearchParams(urlParameters);
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (!_.isNil(searchParams) && searchParams.has("tab")) {
      setNavigationTab(searchParams.get("tab") as MemoTabChoices);
    }
  }, [searchParams]);

  const {
    data: memo,
    mutate: mutateMemoDealDetails,
    error,
    isLoading: isFetchingMemo,
  } = useMemoDealDetails({
    dealId,
    memoId,
  });

  const {
    data: sourceDocument,
    isLoading: isSourceDocumentLoading,
    mutate: mutateSourceDocument,
  } = useSourceDocumentRetrieve({
    dealId,
    memoId,
    sourceDocumentId: Number(selectedSourceDocumentId),
    enabled: !_.isNil(selectedSourceDocumentId),
  });

  useLayoutEffect(() => {
    if (
      !_.isNil(memoSectionIdParam) &&
      memo?.sections
        .map((section) => section.section_id)
        .includes(memoSectionIdParam)
    ) {
      requestAnimationFrame(() => {
        scrollToSelectedAnchor({
          anchor: memoSectionIdParam.toString(),
          navigationIds: ANCHORS,
        });
      });

      setTimeout(
        () =>
          navigate(
            reverse(URLS.DEAL_MEMO, {
              dealId,
              memoId,
            })
          ),
        5000
      );
    }
  }, [memoSectionIdParam, memo?.sections, navigate, dealId, memoId]);

  const { data: user } = useSignedUser();
  const { data: deal } = useDealDetails({ dealId });

  const hasMemoAISections = useMemo(
    () =>
      !_.isNil(memo)
        ? companySettings?.onboarding_company_package_enabled &&
          memo?.sections.some(
            (section) => section.section_type === EMemoSectionType.AI_ANSWER
          )
        : false,
    [memo, companySettings?.onboarding_company_package_enabled]
  );

  const { data: sourceDocuments, mutate: mutateSourceDocuments } =
    useSourceDocumentsForMemo({
      memoId,
      dealId,
      enabled: hasMemoAISections,
    });

  const onUpdate = useCallback(() => {
    mutateSourceDocuments();
    mutateSourceDocument();
    mutateMemoDealDetails();
  }, [mutateSourceDocuments, mutateSourceDocument, mutateMemoDealDetails]);

  const onMemoSectionWrapperUpdate = useCallback(
    () => mutateMemoDealDetails(),
    [mutateMemoDealDetails]
  );

  const disableGenerateButtonOnClickHandler = useCallback(
    ({ disable }: { disable: boolean }) =>
      setDisableGenerateButtonOnClick(disable),
    []
  );

  useRedirectUponFetchFailure({ error, loading: isFetchingMemo });

  usePageTitle(
    deal?.name && memo?.name
      ? `Builders Patch: ${deal.name} - ${memo?.name} - Memo`
      : "Builders Patch: Memo"
  );

  const extractionProcessFinishHandler = useCallback(() => {
    onUpdate();
    disableGenerateButtonOnClickHandler({ disable: false });
  }, [onUpdate, disableGenerateButtonOnClickHandler]);

  useMemoSectionAIExtractionProcessing({
    successEventHandler: extractionProcessFinishHandler,
    failureEventHandler: extractionProcessFinishHandler,
  });

  if (!user || !memo || !deal) {
    return <Loading open />;
  }

  return (
    <Paper
      sx={{
        overflowX: "auto",
        padding: theme.spacing(0, 4, 1, 4),
        "@media print": {
          overflow: "hidden",
          padding: "0",
          flexGrow: 1,
          marginLeft: "0 !important",
          width: "100%",
        },
      }}
    >
      <Stack>
        <Box
          sx={{
            width: "100%",
            maxWidth: BOX_MAX_WIDTH,
            alignSelf: "center",
          }}
        >
          <Box sx={{ marginBottom: theme.spacing(2) }}>
            <PageHeader
              dealId={dealId}
              memoId={memoId}
              memoName={memo?.name}
              dealName={deal?.name}
            />
          </Box>
          <MemoTabs selectedTab={navigationTab} onChange={handleTabChange} />
          {navigationTab === MemoTabChoices.SAVED_PDFS && <SavedPDFsTab />}
          {navigationTab === MemoTabChoices.CONTENT_EDITOR && (
            <>
              <Stack direction="row" justifyContent="space-between">
                <Stack>
                  <Text variant="text4" sx={{ fontWeight: "bold" }}>
                    {deal.name}
                  </Text>
                  <Text variant="text4">{memo.name}</Text>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    data-testid="additional-header-info"
                  >
                    {_.isEmpty(memo?.additional_header_info) ? (
                      <Text
                        variant="text4"
                        data-testid="additional-info-empty-value"
                      >
                        -- additional header info --
                      </Text>
                    ) : (
                      <Text variant="text4" data-testid="additional-info-value">
                        {memo.additional_header_info}
                      </Text>
                    )}
                    <EditDocumentAdditionalHeaderInfoButton
                      dealId={dealId}
                      memoId={memoId}
                      value={memo.additional_header_info}
                      onChange={() => {
                        mutateMemoDealDetails();
                      }}
                    />
                  </Stack>
                </Stack>
                {user.company_logo && (
                  <Image
                    sx={{
                      height: "56px",
                      maxWidth: "50%",
                      marginBottom: 0.25,
                    }}
                    src={user.company_logo}
                  />
                )}
              </Stack>
              <Divider sx={{ marginBottom: theme.spacing(2) }} />
              <Box
                sx={{
                  "& > *": { marginBottom: theme.spacing(2) },
                }}
              >
                {hasMemoAISections && (
                  <>
                    <Stack
                      sx={{
                        background: colors.blue10,
                        padding: theme.spacing(2),
                        borderRadius: 1,
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        gap={0.5}
                        onClick={() =>
                          setIsAIMemoSectionDropdownOpen(
                            (prevState) => !prevState
                          )
                        }
                        sx={{ cursor: "pointer" }}
                      >
                        {isAIMemoSectionDropdownOpen ? (
                          <ArrowDropDownIcon
                            sx={{ width: "20px", height: "20px" }}
                          />
                        ) : (
                          <ArrowRightIcon
                            sx={{ width: "20px", height: "20px" }}
                          />
                        )}
                        <AutoAwesomeOutlinedIcon
                          sx={{ width: "16px", height: "16px" }}
                        />
                        <Text fontSize="16px" fontWeight={700}>
                          AI source documents
                        </Text>
                      </Stack>
                      <Text fontSize="13px" fontWeight={400}>
                        The following source documents are required to
                        auto-complete this template with AI-generated answers.
                      </Text>
                    </Stack>

                    <SourceDocumentCollapse
                      open={isAIMemoSectionDropdownOpen}
                      sourceDocuments={sourceDocuments}
                      memo={memo}
                      hasMemoAISections={hasMemoAISections}
                      onUpdate={onUpdate}
                      disableGenerateButtonOnClick={
                        disableGenerateButtonOnClick
                      }
                      disableGenerateButtonOnClickHandler={
                        disableGenerateButtonOnClickHandler
                      }
                    />
                    <SourceDocumentDrawer
                      open={isSourceDocumentDrawerOpen}
                      sourceDocument={sourceDocument}
                      memoName={memo.name}
                      onUpdate={onUpdate}
                      isLoading={isSourceDocumentLoading}
                    />
                  </>
                )}
                {memo.sections.map((section: IMemoSection) => (
                  <MemoSectionWrapper
                    key={section.section_id}
                    section={section}
                    deal={deal}
                    onUpdate={onMemoSectionWrapperUpdate}
                  />
                ))}
                {memo.include_proforma_pdf && (
                  <AppendixAccordion
                    proformaPDF={proformaPDF}
                    tooltip="This appendix was generated by printing out the
                underwriting pro forma. If you'd like to modify it, you can download the latest pro forma, make any changes to the print area in Excel, and re-upload it to the platform."
                  />
                )}
              </Box>
            </>
          )}
        </Box>
      </Stack>
    </Paper>
  );
};

export default Memo;
